// frontend/src/components/ReportList/ReportList.js
// Purpose: Display previously generated reports
// Desired outcome: Provide a sortable table of reports with metadata and options to view, download, or delete reports with reliable download functionality


import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteReport, fetchReportById, exportReportPdf, exportReportExcel } from '../../state/analysis/analysisSlice';
import logger from '../../utils/logger';
import api from '../../utils/api'; // Import the api module
import './ReportList.css';

const ReportList = ({ reports, isLoading }) => {
  const dispatch = useDispatch();
  const [sortField, setSortField] = useState('generationDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  
  // Function to format date in a readable way
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Handle sorting change
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Sort the reports based on current sort settings
  const sortedReports = [...(reports || [])].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'generationDate':
        comparison = new Date(a.generationDate) - new Date(b.generationDate);
        break;
      default:
        comparison = 0;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Handle report deletion
  const handleDelete = async (reportID) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      setDeletingReportId(reportID);
      
      try {
        await dispatch(deleteReport(reportID)).unwrap();
        logger.info(`Report deleted: ${reportID}`);
      } catch (error) {
        logger.error(`Error deleting report: ${reportID}`, error);
      } finally {
        setDeletingReportId(null);
      }
    }
  };
  
  // Handle report viewing
  const handleView = (reportID) => {
    // Fetch the report data
    dispatch(fetchReportById(reportID))
      .unwrap()
      .then(() => {
        // Navigate to the analysis page
        window.location.href = '/analysis';
      })
      .catch(error => {
        console.error('Error fetching report:', error);
        alert('Failed to load report details.');
      });
  };
  
  // Handle PDF report download with proper error handling
  const handleDownloadPdf = (report) => {
    if (!report.reportID) {
      alert('Report ID not available. Cannot download report.');
      return;
    }
    
    // FIXED: Clear the reportID to prevent duplicate prefix
    // Strip any existing "report-" prefix to avoid duplication
    const cleanReportID = report.reportID.replace(/^report-/, '');
    
    setDownloadingReportId(`${report.reportID}-pdf`);
    
    // Use the api.downloadFile method for PDF download
    api.downloadFile('pdf', cleanReportID)
      .then(() => {
        logger.info(`PDF Report download started successfully: ${cleanReportID}`);
      })
      .catch(error => {
        logger.error(`Error downloading PDF report: ${cleanReportID}`, error);
        alert('Failed to download PDF report. Please try again.');
      })
      .finally(() => {
        setDownloadingReportId(null);
      });
  };
  
  // Handle Excel report download with proper error handling
  const handleDownloadExcel = (report) => {
    if (!report.reportID) {
      alert('Report ID not available. Cannot download report.');
      return;
    }
    
    // FIXED: Clean the reportID to prevent duplicate prefix
    const cleanReportID = report.reportID.replace(/^report-/, '');
    
    setDownloadingReportId(`${report.reportID}-excel`);
    
    // Use the api.downloadFile method for Excel download
    api.downloadFile('excel', cleanReportID)
      .then(() => {
        logger.info(`Excel Report download started successfully: ${cleanReportID}`);
      })
      .catch(error => {
        logger.error(`Error downloading Excel report: ${cleanReportID}`, error);
        alert('Failed to download Excel report. Please try again.');
      })
      .finally(() => {
        setDownloadingReportId(null);
      });
  };

  // Rendering states
  if (isLoading) {
    return <div className="loading">Loading reports...</div>;
  }
  
  if (!reports || reports.length === 0) {
    return <div className="no-reports">No reports generated yet.</div>;
  }

  return (
    <div className="report-list">
      <table>
        <thead>
          <tr>
            <th onClick={() => handleSort('name')} className="sortable">
              Report Name
              {sortField === 'name' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </th>
            <th onClick={() => handleSort('generationDate')} className="sortable">
              Generation Date
              {sortField === 'generationDate' && (
                <span className="sort-indicator">
                  {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedReports.map((report) => (
            <tr key={report.reportID}>
              <td>{report.name}</td>
              <td>{formatDate(report.generationDate)}</td>
              <td className="actions-cell">
                <button
                  className="view-button"
                  onClick={() => handleView(report.reportID)}
                >
                  View
                </button>
                <button
                  className="download-button"
                  onClick={() => handleDownloadPdf(report)}
                  disabled={downloadingReportId === `${report.reportID}-pdf`}
                >
                  {downloadingReportId === `${report.reportID}-pdf` ? 'Downloading...' : 'PDF'}
                </button>
                <button
                  className="download-button"
                  onClick={() => handleDownloadExcel(report)}
                  disabled={downloadingReportId === `${report.reportID}-excel`}
                >
                  {downloadingReportId === `${report.reportID}-excel` ? 'Downloading...' : 'Excel'}
                </button>
                <button
                  className="delete-button"
                  onClick={() => handleDelete(report.reportID)}
                  disabled={deletingReportId === report.reportID}
                >
                  {deletingReportId === report.reportID ? 'Deleting...' : 'Delete'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ReportList;